import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Box, Button, Grid, CircularProgress, Dialog, DialogContent, Typography, Stack } from '@mui/material';
import { Send } from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../redux/root-reducer';
import type { IInfluencerTier } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    getBuzzBridgeProfile,
    getOffsiteCampaignDetails,
    postInfluencerGuestOAuthLink,
    postInfluencerOAuthConnect,
    requestToJoinCampaign,
    saveManualInstagramLink,
    updateBuzzBridgeProfile,
} from '../../redux/buzzbridge/actions';
import {
    buzzBridgeProfileSelector,
    currentCampaignSelector,
    gettingBuzzBridgeProfileSelector,
    gettingCampaignDetailsSelector,
    requestingToJoinCampaignSelector,
} from '../../redux/buzzbridge/selectors';
import { acceptCampaignInviteUrl, declineCampaignInviteUrl } from '../../configurations/api/url';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { createUserProfile, getPhoneVerificationCode } from '../../redux/user/actions';
import { toggleAuthModal } from '../../redux/authentication/action';
import { validate } from '../../utils/validators';
import { APP_ROUTES } from '../../types/global/routes.types';
import { dashboardHubPath } from '../../utils/dashboardHub';
import { showToastMessage, useDispatchPromise, waitForAuthenticationToken } from '../../utils/AppUtils';
import { creatorMeetsCampaignTierFollowerMinimum } from '../../utils/ugcCampaignConfig';
import { store } from '../../redux/store';
import { useAuth } from '../../module/AuthContext';
import { formatSocialHandle } from '../../utils/socialHandle';
import type { BuzzGuestPlatformId, InfluencerOAuthConnectedMap } from '../../utils/influencerPrelink';
import {
    clearInfluencerPrelinkSession,
    clearPendingBuzzBridgeApplyCampaignId,
    loadInfluencerPrelinkFromSession,
    saveInfluencerPrelinkToSession,
    setPendingBuzzBridgeApplyCampaignId,
    getPendingBuzzBridgeApplyCampaignId,
} from '../../utils/influencerPrelink';
import type { IBuzzBridgeProfileResponse, IInfluencer } from '../../redux/buzzbridge/types';
import { CampaignDetailHero } from './CampaignDetailHero';
import { CampaignDetailContent } from './CampaignDetailContent';
import { CampaignDetailRoles } from './CampaignDetailRoles';
import { CampaignReferralEarnSidebar } from './CampaignReferralEarnSidebar';
import { isCampaignReferralEarnVisible } from '../../utils/campaignReferralShare';
import { connectedPlatformsUrl } from '../../configurations/api/url';
import { CampaignApplicationForm, IInstagramManualData } from './CampaignApplicationForm';
import { EditBasicInfoModal } from './components/EditBasicInfoModal';
import { AccountSubscriptionModal } from '../../pages/AccountSettings/AccountSubscriptionModal';
import {
    creatorEffectiveEntitlementsSelector,
    hasActiveCreatorThspianPlanSelector,
    plansSelector,
    userSubscriptionSelector,
} from '../../pages/EsSettings/selector';
import {
    formatCreatorTrialActiveToast,
    isCreatorTrialAvailable,
    pickCreatorTrial,
    resolveCreatorTrialApplyCtaLabel,
} from '../../utils/creatorSubscriptionTrial';
import { isFlutterwaveAbandonedReturn } from '../../utils/flutterwaveReturn';
import {
    verifyPaystackSubscription,
    verifyFlutterwaveSubscription,
    setSubscription,
} from '../../redux/subscription/actions';
import { useCreatorCampaignPayoutDisplay } from '../../hook/useCreatorCampaignPayoutDisplay';
import { formatCurrency } from '../../constants/currency';
import { EMPTY_MANUAL_PLATFORM_LINK, isManualPlatformLinkComplete } from '../../utils/manualPlatformLinkUtils';
import { manualLinkDataForSignupApi } from '../../utils/influencerManualLinkSave';
import { shouldAutoApplyGuestOnSignIn } from '../../utils/guestApplyAuthUtils';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';
import { CampaignApplicationOTP } from './CampaignApplicationOTP';
import { CampaignApplicationSuccess } from './CampaignApplicationSuccess';
import {
    getPublicCampaignApplyLabel,
    isPublicCampaignApplyEnabled,
    isPublicCampaignParticipationBlocked,
    resolveParticipationGate,
} from '../../utils/campaignParticipationGate';
import {
    syncUploadRequiresAutoLink,
    resolveRequiredOauthPlatforms,
    type SyncOauthPlatformId,
} from '../../utils/syncUploadCampaign';
import {
    premiumCampaignUnlockEntitlements,
    reconcilePremiumAccessWithLiveEntitlements,
} from '../../utils/planEntitlements';

export const BuzzBridgeJobDetails = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const { themeStyle, device } = useThemeContext();
    const t = theme[themeStyle];

    const [searchParams, setSearchParams] = useSearchParams();
    const authToken = useSelector((state: RootState) => state.authentication.token);
    const { isSignedIn, refreshAuth } = useAuth();
    const campaign = useSelector(currentCampaignSelector);
    const loading = useSelector(gettingCampaignDetailsSelector);
    const requesting = useSelector(requestingToJoinCampaignSelector);
    const buzzProfile = useSelector(buzzBridgeProfileSelector);
    const gettingBuzzProfile = useSelector(gettingBuzzBridgeProfileSelector);
    const configTiers = useSelector((state: RootState) => state.configuration.influencerTiers);
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const requiresSyncAutoLink = syncUploadRequiresAutoLink(campaign);
    const instagramAutoLinkEnabled = requiresSyncAutoLink || buzzBridgeConfig?.enableAutoLink?.Instagram !== false;
    const hasActiveSubscription = useSelector(hasActiveCreatorThspianPlanSelector);
    const liveCreatorEntitlements = useSelector(creatorEffectiveEntitlementsSelector);
    const catalogPlans = useSelector(plansSelector);
    const userSubscription = useSelector(userSubscriptionSelector);
    const creatorTrial = useMemo(
        () => pickCreatorTrial(userSubscription, catalogPlans),
        [userSubscription, catalogPlans],
    );
    const trialAvailable = isCreatorTrialAvailable(creatorTrial);
    const effectivePremiumAccess = useMemo(
        () => reconcilePremiumAccessWithLiveEntitlements(campaign?.premium_access, liveCreatorEntitlements),
        [campaign?.premium_access, liveCreatorEntitlements],
    );
    const { breakdown: creatorPayoutBreakdown } = useCreatorCampaignPayoutDisplay(campaign ?? ({} as any));
    const subscriptionSaveAmountFormatted = (() => {
        const saveUsd = creatorPayoutBreakdown?.subscribe_to_save_usd;
        return saveUsd != null && Number.isFinite(saveUsd) ? formatCurrency(saveUsd) : undefined;
    })();
    const premiumUnlockEntitlements = useMemo(
        () => premiumCampaignUnlockEntitlements(effectivePremiumAccess ?? campaign?.premium_access),
        [effectivePremiumAccess, campaign?.premium_access],
    );

    const [guestApplyOpen, setGuestApplyOpen] = useState(false);
    const [guestPhase, setGuestPhase] = useState<'form' | 'otp' | 'success'>('form');
    const [loggedInConnectOpen, setLoggedInConnectOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [profilePicNudgeOpen, setProfilePicNudgeOpen] = useState(false);
    const [editBasicInfoForApplyOpen, setEditBasicInfoForApplyOpen] = useState(false);
    const [subscriptionNudgeOpen, setSubscriptionNudgeOpen] = useState(false);
    /** When true, skip is not allowed — premium campaign requires an active Pro plan. */
    const [subscriptionNudgeRequired, setSubscriptionNudgeRequired] = useState(false);
    /** When true, nudge is for a gifted campaign — any subscription suffices (not Pro-specific). */
    const [giftedSubNudge, setGiftedSubNudge] = useState(false);
    const [shippingNudgeOpen, setShippingNudgeOpen] = useState(false);
    const [guestFirstName, setGuestFirstName] = useState('');
    const [guestLastName, setGuestLastName] = useState('');
    const [guestInstagramManualData, setGuestInstagramManualData] = useState<IInstagramManualData>({
        ...EMPTY_MANUAL_PLATFORM_LINK,
    });
    const [creatorInstagramManualData, setCreatorInstagramManualData] = useState<IInstagramManualData>({
        ...EMPTY_MANUAL_PLATFORM_LINK,
    });
    const [guestEmail, setGuestEmail] = useState('');
    const [guestPassword, setGuestPassword] = useState('');
    const [guestPhone, setGuestPhone] = useState('');
    const [guestOAuthConnected, setGuestOAuthConnected] = useState<InfluencerOAuthConnectedMap>({});

    const guestInstagramManualFilled = isManualPlatformLinkComplete(guestInstagramManualData);
    const guestInstagramReady = instagramAutoLinkEnabled
        ? Boolean(guestOAuthConnected.instagram)
        : guestInstagramManualFilled;
    const [creatorOAuthConnected, setCreatorOAuthConnected] = useState<InfluencerOAuthConnectedMap>({});
    const [loadingConnectedPlatforms, setLoadingConnectedPlatforms] = useState(false);
    const [oauthConnectingGuest, setOauthConnectingGuest] = useState<BuzzGuestPlatformId | null>(null);
    const [oauthConnectingCreator, setOauthConnectingCreator] = useState<BuzzGuestPlatformId | null>(null);
    const [guestSubmitBusy, setGuestSubmitBusy] = useState(false);
    const [creatorApplyBusy, setCreatorApplyBusy] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpResendInProgress, setOtpResendInProgress] = useState(false);
    const pendingApplyRunRef = useRef(0);
    const [savedInfluencerId, setSavedInfluencerId] = useState<string | undefined>();

    const influencer = buzzProfile?.data?.influencer;
    const activeInfluencerId = influencer?._id ?? savedInfluencerId;
    const hasInfluencerAccount = Boolean(isSignedIn && activeInfluencerId);
    const isGiftedCampaign = campaign?.reward === 'gifted';
    const isGiftedWithPhysicalProduct = isGiftedCampaign && Boolean(campaign?.physical_product);
    const hasShippingAddress = Boolean(
        influencer?.shipping_address?.addressLine1?.trim() && influencer?.shipping_address?.country?.trim(),
    );
    // True when the creator has an active subscription OR a cancelled one whose paid period hasn't expired yet.
    const hasAnyPaidSubscriptionAccess = useMemo(() => {
        if (hasActiveSubscription) return true;
        const status = (userSubscription?.status ?? '').toLowerCase();
        if (status !== 'canceled' && status !== 'cancelled') return false;
        const endRaw =
            userSubscription?.current_period_end ?? userSubscription?.subscription_details?.current_period_end;
        if (!endRaw) return false;
        const endMs = typeof endRaw === 'number' ? endRaw * 1000 : Date.parse(String(endRaw));
        return Number.isFinite(endMs) && endMs > Date.now();
    }, [hasActiveSubscription, userSubscription]);

    useEffect(() => {
        if (!campaignId) return;
        // Refetch when auth changes so premium_access / sync gates match signed-in entitlements.
        dispatch(getOffsiteCampaignDetails({ campaignId }));
    }, [campaignId, isSignedIn, authToken, dispatch]);

    useEffect(() => {
        if (isSignedIn) {
            dispatch(setSubscription());
        }
    }, [isSignedIn, dispatch]);

    // Verify Paystack / Flutterwave subscription when redirected back from hosted checkout
    useEffect(() => {
        const transactionId = searchParams.get('transaction_id');
        const txRef = searchParams.get('tx_ref');
        const reference = searchParams.get('reference') ?? searchParams.get('trxref');

        const afterVerified = async (sub?: any) => {
            setSearchParams({}, { replace: true });
            const updatedCard = Boolean(sub?.updated_card);
            const isTrial = Boolean(sub?.status === 'trialing' || sub?.subscription_details?.is_trial);
            const trialDays = sub?.subscription_details?.trial_days ?? sub?.trial?.days ?? sub?.trial_days;
            showToastMessage(
                updatedCard
                    ? 'Card updated on Flutterwave'
                    : isTrial
                    ? formatCreatorTrialActiveToast(trialDays)
                    : 'Subscription activated!',
                'success',
            );
            if (campaignId) {
                try {
                    await dispatchPromise(getOffsiteCampaignDetails({ campaignId }));
                } catch {
                    /* keep local reconcile from get-my-plan */
                }
            }
            const pendingCampaign = getPendingBuzzBridgeApplyCampaignId();
            if (pendingCampaign && pendingCampaign === campaignId) {
                void proceedToApply();
            }
        };

        if (isFlutterwaveAbandonedReturn(searchParams)) {
            setSearchParams({}, { replace: true });
            return;
        }

        if (transactionId || txRef) {
            dispatchPromise(
                verifyFlutterwaveSubscription({
                    transaction_id: transactionId ?? undefined,
                    tx_ref: txRef ?? reference ?? undefined,
                    reference: txRef ?? reference ?? undefined,
                }),
            )
                .then(sub => afterVerified(sub))
                .catch((err: any) => {
                    const message =
                        err?.data?.message ?? err?.message ?? 'Could not verify subscription. Please contact support.';
                    showToastMessage(message, 'error');
                });
            return;
        }

        if (!reference) return;
        dispatchPromise(verifyPaystackSubscription({ reference }))
            .then(sub => afterVerified(sub))
            .catch((err: any) => {
                const message =
                    err?.data?.message ?? err?.message ?? 'Could not verify subscription. Please contact support.';
                showToastMessage(message, 'error');
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isSignedIn && authToken?.trim()) {
            // Guest / logged-in apply modals fetch profile as part of submit — avoid takeLatest cancelling promises.
            if (guestApplyOpen && (guestPhase === 'otp' || guestPhase === 'form')) return;
            if (loggedInConnectOpen) return;
            dispatch(getBuzzBridgeProfile({ skipRefresh: false }));
        }
    }, [isSignedIn, authToken, dispatch, guestApplyOpen, guestPhase, loggedInConnectOpen]);

    useEffect(() => {
        if (loggedInConnectOpen && isSignedIn && authToken?.trim()) {
            dispatch(getBuzzBridgeProfile({ skipRefresh: false }));
        }
    }, [loggedInConnectOpen, isSignedIn, authToken, dispatch]);

    useEffect(() => {
        if (influencer?._id) {
            setSavedInfluencerId(influencer._id);
        }
    }, [influencer?._id]);

    const fetchConnectedPlatforms = useCallback(async (): Promise<InfluencerOAuthConnectedMap> => {
        if (!authToken?.trim()) {
            setCreatorOAuthConnected({});
            return {};
        }
        setLoadingConnectedPlatforms(true);
        try {
            const res = await axios.get<{
                data: { platforms: { platform: BuzzGuestPlatformId; platform_username: string }[] };
            }>(connectedPlatformsUrl, { headers: { Authorization: `Bearer ${authToken}` } });
            const platforms = res.data?.data?.platforms ?? [];
            const map: InfluencerOAuthConnectedMap = {};
            platforms.forEach(p => {
                map[p.platform] = { label: p.platform_username };
            });
            setCreatorOAuthConnected(map);
            return map;
        } catch {
            setCreatorOAuthConnected({});
            return {};
        } finally {
            setLoadingConnectedPlatforms(false);
        }
    }, [authToken]);

    // Keep OAuth link status fresh for sync_upload notices / apply gates.
    useEffect(() => {
        if (!isSignedIn || !authToken?.trim() || !requiresSyncAutoLink) return;
        void fetchConnectedPlatforms();
    }, [isSignedIn, authToken, requiresSyncAutoLink, fetchConnectedPlatforms, campaignId]);

    // Platform connectivity + tier + API call — called after all pre-checks pass
    const proceedToApply = useCallback(async () => {
        if (!campaignId) return;

        // Prefer latest Redux campaign (fresh premium_access after sign-in / subscribe),
        // then reconcile with live get-my-plan entitlements (campaign payload can lag).
        const latestCampaign = (store.getState() as RootState).buzzbridge?.currentCampaign ?? campaign;
        const liveEntitlements = creatorEffectiveEntitlementsSelector(store.getState() as RootState);
        const premiumAccess = reconcilePremiumAccessWithLiveEntitlements(
            latestCampaign?.premium_access,
            liveEntitlements,
        );
        if (premiumAccess?.premium && !premiumAccess.can_apply && !latestCampaign?.app_install?.enabled) {
            const code = premiumAccess.blocked_reason_code;
            if (code === 'subscription_required' || code === 'login_required') {
                setSubscriptionNudgeRequired(
                    Boolean(premiumAccess.subscription_required || code === 'subscription_required'),
                );
                setSubscriptionNudgeOpen(true);
                return;
            }
            if (code === 'connected_accounts_required' || code === 'instagram_automation_scopes') {
                setCreatorOAuthConnected({});
                setLoggedInConnectOpen(true);
                fetchConnectedPlatforms();
                showToastMessage(
                    premiumAccess.blocked_reason_message ||
                        'Connect your accounts with the required permissions to apply.',
                    'info',
                );
                return;
            }
            showToastMessage(
                premiumAccess.blocked_reason_message || 'You do not meet the requirements for this premium campaign.',
                'error',
            );
            return;
        }

        const OAUTH_PLATFORMS: BuzzGuestPlatformId[] = ['instagram', 'tiktok', 'youtube'];
        const requiredPlatforms = (latestCampaign?.platforms ?? []).filter((p: string): p is BuzzGuestPlatformId =>
            (OAUTH_PLATFORMS as string[]).includes(String(p)),
        );
        const platformsNeedingLink: BuzzGuestPlatformId[] =
            requiredPlatforms.length > 0 ? requiredPlatforms : requiresSyncAutoLink ? ['instagram'] : [];

        // Sync upload / premium always require OAuth auto-link for remaining platforms.
        if (requiresSyncAutoLink || premiumAccess?.connected_accounts_required) {
            const connectedMap =
                Object.keys(creatorOAuthConnected).length > 0 ? creatorOAuthConnected : await fetchConnectedPlatforms();
            const linkTargets: BuzzGuestPlatformId[] = platformsNeedingLink.length
                ? platformsNeedingLink
                : resolveRequiredOauthPlatforms(latestCampaign);
            const missingOauth = linkTargets.filter((p: BuzzGuestPlatformId) => !connectedMap[p]);
            if (missingOauth.length > 0) {
                setLoggedInConnectOpen(true);
                showToastMessage(
                    `Connect auto-linked ${missingOauth.join(', ')} account${
                        missingOauth.length > 1 ? 's' : ''
                    } to continue.`,
                    'info',
                );
                return;
            }
        }

        const missingPlatforms = requiredPlatforms.filter(
            (p: BuzzGuestPlatformId) => !influencer?.[p as keyof typeof influencer],
        );
        if (missingPlatforms.length > 0) {
            setCreatorOAuthConnected({});
            setLoggedInConnectOpen(true);
            fetchConnectedPlatforms().then(connectedMap => {
                const stillMissing = missingPlatforms.filter((p: BuzzGuestPlatformId) => !connectedMap[p]);
                if (stillMissing.length > 0) {
                    showToastMessage(
                        `Connect your ${stillMissing.join(', ')} account${
                            stillMissing.length > 1 ? 's' : ''
                        } to apply for this campaign.`,
                        'info',
                    );
                }
            });
            return;
        }

        const TIER_MIN: Record<string, number> =
            configTiers.length > 0
                ? Object.fromEntries(configTiers.map((t: IInfluencerTier) => [t.id, t.min_followers]))
                : { micro: 300, mid: 50_000, macro: 200_000, 'top-tier': 1_000_000 };
        const TIER_LABEL: Record<string, string> =
            configTiers.length > 0
                ? Object.fromEntries(
                      configTiers.map((t: IInfluencerTier) => [t.id, t.reach.split('–')[0].split('-')[0]]),
                  )
                : { micro: '300', mid: '50K', macro: '200K', 'top-tier': '1M' };
        const campaignTier = latestCampaign?.influencer_tier;
        const tierMin = campaignTier ? TIER_MIN[campaignTier] : null;
        if (campaignTier && tierMin != null) {
            const meetsTier = creatorMeetsCampaignTierFollowerMinimum({
                minFollowers: tierMin,
                requiredPlatforms: requiredPlatforms,
                getFollowers: platform => {
                    const stats = influencer?.[platform as keyof typeof influencer] as
                        | { followers?: number }
                        | undefined;
                    return stats?.followers;
                },
            });
            if (!meetsTier) {
                const multi = requiredPlatforms.length > 1;
                showToastMessage(
                    multi
                        ? `This campaign requires ${campaignTier} creators (${TIER_LABEL[campaignTier]}+ followers) on at least one requested platform.`
                        : `This campaign requires ${campaignTier} creators (${
                              TIER_LABEL[campaignTier]
                          }+ followers). Your ${
                              requiredPlatforms[0]
                                  ? requiredPlatforms[0].charAt(0).toUpperCase() + requiredPlatforms[0].slice(1)
                                  : 'connected'
                          } account doesn't meet the minimum yet.`,
                    'error',
                );
                return;
            }
        }

        try {
            await dispatchPromise(requestToJoinCampaign({ campaignId }));
            setGuestApplyOpen(false);
            setGuestPhase('form');
            setLoggedInConnectOpen(false);
            setSuccessOpen(true);
        } catch {
            // globalErrorHandler + toast already shown
        }
    }, [
        campaign,
        campaignId,
        configTiers,
        creatorOAuthConnected,
        dispatchPromise,
        fetchConnectedPlatforms,
        influencer,
        requiresSyncAutoLink,
    ]);

    /**
     * After guest Instagram + signup/OTP (or sign-in), run the same gates as logged-in apply:
     * subscription (required) → remaining OAuth auto-links → submit.
     */
    const continueAfterGuestAuth = useCallback(async () => {
        if (!campaignId) return;
        try {
            await waitForAuthenticationToken();
            await dispatchPromise(getBuzzBridgeProfile({ skipRefresh: false }));
            if (guestInstagramManualData.headshotFile) {
                const profile = (store.getState() as RootState).buzzbridge?.buzzProfile as
                    | IBuzzBridgeProfileResponse
                    | null
                    | undefined;
                const influencerId = profile?.data?.influencer?._id;
                if (influencerId) {
                    await dispatchPromise(
                        saveManualInstagramLink({
                            data: guestInstagramManualData,
                            influencerId,
                            uploadHeadshotOnly: true,
                        }),
                    );
                }
            }
            await dispatchPromise(getOffsiteCampaignDetails({ campaignId }));
            await proceedToApply();
        } catch {
            showToastMessage('Signed in. Tap Apply to finish your application.', 'info');
        }
    }, [campaignId, dispatchPromise, guestInstagramManualData, proceedToApply]);

    useEffect(() => {
        if (
            !shouldAutoApplyGuestOnSignIn({
                isSignedIn,
                guestApplyOpen,
                guestPhase,
                pendingApplyCampaignId: getPendingBuzzBridgeApplyCampaignId(),
                campaignId,
            })
        ) {
            return;
        }

        // Existing account signed in during guest form — continue with subscribe → OAuth → apply.
        void continueAfterGuestAuth();
    }, [isSignedIn, guestApplyOpen, guestPhase, campaignId, continueAfterGuestAuth]);

    /** Guest IG OAuth returned `account_exists` — after sign-in, continue gated apply. */
    useEffect(() => {
        if (!isSignedIn || !authToken?.trim() || !campaignId) return;
        const pending = getPendingBuzzBridgeApplyCampaignId();
        if (!pending || pending !== campaignId) return;

        const runId = ++pendingApplyRunRef.current;
        let cancelled = false;

        (async () => {
            try {
                clearPendingBuzzBridgeApplyCampaignId();
                setGuestApplyOpen(false);
                await continueAfterGuestAuth();
                if (cancelled || runId !== pendingApplyRunRef.current) return;
            } catch {
                if (!cancelled && runId === pendingApplyRunRef.current) {
                    showToastMessage('Signed in. Tap Apply to finish joining this campaign.', 'info');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isSignedIn, authToken, campaignId, continueAfterGuestAuth]);

    const handleApply = useCallback(async () => {
        if (!campaignId) return;

        if (!isPublicCampaignApplyEnabled(campaign)) {
            const gate = resolveParticipationGate(campaign);
            showToastMessage(gate?.blocked_reason_message?.trim() || 'This campaign is at capacity.', 'info');
            return;
        }

        if (!isSignedIn) {
            setGuestApplyOpen(true);
            return;
        }

        if (!hasInfluencerAccount) {
            setCreatorOAuthConnected({});
            setLoggedInConnectOpen(true);
            fetchConnectedPlatforms();
            return;
        }

        const premiumAccess = effectivePremiumAccess ?? campaign?.premium_access;
        if (
            premiumAccess?.premium &&
            premiumAccess.can_apply === false &&
            premiumAccess.blocked_reason_code === 'subscription_required' &&
            !campaign?.app_install?.enabled
        ) {
            setSubscriptionNudgeRequired(true);
            setSubscriptionNudgeOpen(true);
            return;
        }

        if (!influencer?.profile_picture) {
            setProfilePicNudgeOpen(true);
            return;
        }

        if (isGiftedCampaign && !hasAnyPaidSubscriptionAccess && !campaign?.app_install?.enabled) {
            setGiftedSubNudge(true);
            setSubscriptionNudgeOpen(true);
            return;
        }

        if (isGiftedWithPhysicalProduct && !hasShippingAddress) {
            setShippingNudgeOpen(true);
            return;
        }

        if (premiumAccess?.premium && !premiumAccess.can_apply) {
            await proceedToApply();
            return;
        }

        if (!hasActiveSubscription) {
            setSubscriptionNudgeRequired(Boolean(premiumAccess?.subscription_required));
            setSubscriptionNudgeOpen(true);
            return;
        }

        await proceedToApply();
    }, [
        campaign,
        campaignId,
        effectivePremiumAccess,
        fetchConnectedPlatforms,
        hasActiveSubscription,
        hasAnyPaidSubscriptionAccess,
        hasInfluencerAccount,
        hasShippingAddress,
        influencer,
        isGiftedCampaign,
        isGiftedWithPhysicalProduct,
        isSignedIn,
        proceedToApply,
    ]);

    const subscriptionRequiredGate = useMemo(() => {
        const premiumAccess = effectivePremiumAccess ?? campaign?.premium_access;
        return Boolean(
            isSignedIn &&
                premiumAccess?.premium &&
                premiumAccess.can_apply === false &&
                premiumAccess.blocked_reason_code === 'subscription_required',
        );
    }, [isSignedIn, effectivePremiumAccess, campaign?.premium_access]);

    const showTrialApplyCta = Boolean(subscriptionRequiredGate && trialAvailable);

    const openPlansModal = useCallback(() => {
        setSubscriptionNudgeRequired(true);
        setSubscriptionNudgeOpen(true);
    }, []);

    const handleApplyOrTrialCta = useCallback(() => {
        MixPanel.track(MIXPANEL_EVENT.JOIN_CAMPAIGN_CLICK, {
            buttonName: showTrialApplyCta ? 'START TRIAL APPLY' : 'JOIN CAMPAIGN APPLY',
            buttonAction: subscriptionRequiredGate ? 'JOIN_CAMPAIGN_SUBSCRIPTION_GATE' : 'JOIN_CAMPAIGN',
            campaignId,
            campaignTitle: campaign?.title,
            source: 'campaign_detail',
            subscriptionRequired: Boolean(subscriptionRequiredGate),
            trialCta: showTrialApplyCta,
        });
        if (subscriptionRequiredGate) {
            openPlansModal();
            return;
        }
        void handleApply();
    }, [subscriptionRequiredGate, openPlansModal, handleApply, showTrialApplyCta, campaignId, campaign?.title]);

    const handleSaveBasicInfoForApply = useCallback(
        (changes: Partial<IInfluencer>) => {
            if (influencer?._id) {
                dispatch(updateBuzzBridgeProfile({ documentId: influencer._id, payload: changes }));
            }
            setEditBasicInfoForApplyOpen(false);
            void proceedToApply();
        },
        [dispatch, influencer, proceedToApply],
    );

    const [inviteActionBusy, setInviteActionBusy] = useState(false);

    const handleAcceptPendingInvite = useCallback(async () => {
        const inviteId = campaign?.viewer_pending_invite?._id;
        if (!inviteId || !authToken || !campaignId) return;
        setInviteActionBusy(true);
        try {
            await axios.patch(
                acceptCampaignInviteUrl(inviteId),
                {},
                { headers: { Authorization: `Bearer ${authToken}` } },
            );
            showToastMessage('Invite accepted', 'success');
            dispatch(getOffsiteCampaignDetails({ campaignId }));
            dispatch(getBuzzBridgeProfile({}));
        } catch (err: any) {
            showToastMessage(err?.response?.data?.message || 'Could not accept invite', 'error');
        } finally {
            setInviteActionBusy(false);
        }
    }, [authToken, campaign?.viewer_pending_invite?._id, campaignId, dispatch]);

    const handleDeclinePendingInvite = useCallback(async () => {
        const inviteId = campaign?.viewer_pending_invite?._id;
        if (!inviteId || !authToken || !campaignId) return;
        setInviteActionBusy(true);
        try {
            await axios.patch(
                declineCampaignInviteUrl(inviteId),
                {},
                { headers: { Authorization: `Bearer ${authToken}` } },
            );
            showToastMessage('Invite declined', 'info');
            dispatch(getOffsiteCampaignDetails({ campaignId }));
        } catch (err: any) {
            showToastMessage(err?.response?.data?.message || 'Could not decline invite', 'error');
        } finally {
            setInviteActionBusy(false);
        }
    }, [authToken, campaign?.viewer_pending_invite?._id, campaignId, dispatch]);

    const handleGuestOAuthConnecting = useCallback((platform: BuzzGuestPlatformId) => {
        setOauthConnectingGuest(platform);
    }, []);

    const handleGuestOAuthFailed = useCallback((_platform: BuzzGuestPlatformId) => {
        setOauthConnectingGuest(null);
    }, []);

    const handleGuestOAuthCode = useCallback(
        async (code: string, provider: string, platform: BuzzGuestPlatformId) => {
            if (!campaignId) return;
            if (platform !== 'instagram') {
                setOauthConnectingGuest(null);
                return;
            }
            try {
                const existing = loadInfluencerPrelinkFromSession();
                const result = (await dispatchPromise(
                    postInfluencerGuestOAuthLink({
                        code,
                        provider,
                        campaign_id: campaignId,
                        influencer_id: existing?.influencer_id,
                        claim_token: existing?.claim_token,
                    }),
                )) as any;

                if (result.account_exists) {
                    clearInfluencerPrelinkSession();
                    setGuestEmail('');
                    setGuestPassword('');
                    setGuestPhone('');
                    setGuestOAuthConnected({});
                    setGuestPhase('form');
                    setOauthConnectingGuest(null);
                    setGuestApplyOpen(false);
                    setPendingBuzzBridgeApplyCampaignId(campaignId);
                    showToastMessage(
                        'This Instagram is already on an account. Sign in to continue — we’ll submit your application right after.',
                        'info',
                    );
                    dispatch(toggleAuthModal(true));
                    return;
                }

                if (result.claim_token) {
                    saveInfluencerPrelinkToSession({
                        influencer_id: result.influencer_id,
                        claim_token: result.claim_token,
                    });
                }
                const hint = result.connectedHint?.[platform];
                const label = hint ? formatSocialHandle(hint) : 'Connected';
                setGuestOAuthConnected(prev => ({ ...prev, [platform]: { label } }));
                showToastMessage(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected`, 'success');
            } catch (err: any) {
                const msg =
                    err?.data?.message ||
                    err?.response?.data?.message ||
                    (typeof err?.message === 'string' ? err.message : null) ||
                    'Could not connect this account. Try again.';
                showToastMessage(msg, 'error');
            } finally {
                setOauthConnectingGuest(null);
            }
        },
        [campaignId, dispatch, dispatchPromise],
    );

    const handleCreatorOAuthConnecting = useCallback((platform: BuzzGuestPlatformId) => {
        setOauthConnectingCreator(platform);
    }, []);

    const handleCreatorOAuthFailed = useCallback((_platform: BuzzGuestPlatformId) => {
        setOauthConnectingCreator(null);
    }, []);

    const handleCreatorOAuthCode = useCallback(
        async (code: string, provider: string, platform: BuzzGuestPlatformId) => {
            try {
                await dispatchPromise(postInfluencerOAuthConnect({ code, provider }));
                await dispatchPromise(getBuzzBridgeProfile({ skipRefresh: false }));
                setCreatorOAuthConnected(prev => ({ ...prev, [platform]: { label: 'Connected' } }));
                showToastMessage(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected`, 'success');
            } catch (err: any) {
                const msg =
                    err?.data?.message ||
                    err?.response?.data?.message ||
                    (typeof err?.message === 'string' ? err.message : null) ||
                    'Could not connect this account. Try again.';
                showToastMessage(msg, 'error');
            } finally {
                setOauthConnectingCreator(null);
            }
        },
        [dispatchPromise],
    );

    const guestSignupPayload = useCallback(
        () => ({
            firstName: guestFirstName.trim(),
            lastName: guestLastName.trim(),
            email: guestEmail.trim(),
            password: guestPassword,
            phoneNumber: guestPhone.trim(),
            verificationChannel: 'email' as const,
            ...(!instagramAutoLinkEnabled && guestInstagramManualFilled
                ? { instagramManualData: manualLinkDataForSignupApi(guestInstagramManualData) }
                : {}),
        }),
        [
            guestFirstName,
            guestLastName,
            guestEmail,
            guestPassword,
            guestPhone,
            instagramAutoLinkEnabled,
            guestInstagramManualFilled,
            guestInstagramManualData,
        ],
    );

    const handleGuestSubmit = useCallback(async () => {
        if (!guestFirstName.trim()) {
            showToastMessage('Please enter your first name.', 'error');
            return;
        }
        if (!guestLastName.trim()) {
            showToastMessage('Please enter your last name.', 'error');
            return;
        }
        if (!guestEmail.trim() || !validate.validateEmail(guestEmail.trim())) {
            showToastMessage('Please enter a valid email address.', 'error');
            return;
        }
        if (!guestPhone.trim() || !validate.validatePhoneNumber(guestPhone.trim())) {
            showToastMessage('Please enter a valid phone number.', 'error');
            return;
        }
        const pwOk =
            validate.validatePasswordLength(guestPassword) &&
            validate.validatePasswordCase(guestPassword) &&
            validate.validatePasswordNumber(guestPassword);
        if (!pwOk) {
            showToastMessage('Password needs 8+ chars, uppercase, lowercase, and a number.', 'error');
            return;
        }
        if (!instagramAutoLinkEnabled) {
            if (!guestInstagramManualFilled) {
                showToastMessage('Please complete all manual Instagram stats and upload your headshot.', 'error');
                return;
            }
        } else if (!loadInfluencerPrelinkFromSession()) {
            showToastMessage('Connect at least one social account first.', 'error');
            return;
        }
        setGuestSubmitBusy(true);
        try {
            await dispatchPromise(getPhoneVerificationCode(guestSignupPayload()));
            setGuestPhase('otp');
        } catch {
            // toast via saga
        } finally {
            setGuestSubmitBusy(false);
        }
    }, [
        guestFirstName,
        guestLastName,
        guestEmail,
        guestPhone,
        guestPassword,
        guestSignupPayload,
        dispatchPromise,
        instagramAutoLinkEnabled,
        guestInstagramManualFilled,
    ]);

    const handleGuestVerifyOTP = useCallback(
        async (code: string[]) => {
            if (!campaignId) return;
            setOtpLoading(true);
            try {
                await dispatchPromise(createUserProfile({ ...guestSignupPayload(), code: code.join('') }));
                refreshAuth();
                setGuestApplyOpen(false);
                await continueAfterGuestAuth();
            } catch {
                // toast via saga
            } finally {
                setOtpLoading(false);
            }
        },
        [campaignId, guestSignupPayload, dispatchPromise, refreshAuth, continueAfterGuestAuth],
    );

    const handleGuestResendOTP = useCallback(async () => {
        setOtpResendInProgress(true);
        try {
            await dispatchPromise(getPhoneVerificationCode({ ...guestSignupPayload(), resendToken: true }));
        } catch {
            // toast via saga
        } finally {
            setOtpResendInProgress(false);
        }
    }, [guestSignupPayload, dispatchPromise]);

    const handleCreatorManualPlatformSave = useCallback(
        async (platform: 'instagram' | 'tiktok' | 'youtube', data: IInstagramManualData) => {
            if (!campaignId) return;
            const newId = await dispatchPromise(
                saveManualInstagramLink({
                    platform,
                    data,
                    influencerId: activeInfluencerId,
                }),
            );
            if (typeof newId === 'string') {
                setSavedInfluencerId(newId);
            }
            if (platform === 'instagram') {
                await dispatchPromise(requestToJoinCampaign({ campaignId }));
                setLoggedInConnectOpen(false);
                setCreatorOAuthConnected({});
                setSuccessOpen(true);
            }
        },
        [campaignId, activeInfluencerId, dispatchPromise],
    );

    const handleLoggedInApplySubmit = useCallback(async () => {
        if (!campaignId) return;
        if ((requiresSyncAutoLink || instagramAutoLinkEnabled) && Object.keys(creatorOAuthConnected).length === 0) {
            showToastMessage(
                requiresSyncAutoLink
                    ? 'This sync campaign requires OAuth auto-linked accounts. Connect at least one platform first.'
                    : 'Connect at least one social account with OAuth first.',
                'error',
            );
            return;
        }
        if (!requiresSyncAutoLink && !instagramAutoLinkEnabled && !influencer?.instagram?.username) {
            showToastMessage('No Instagram profile found. Please complete your profile setup.', 'error');
            return;
        }
        setCreatorApplyBusy(true);
        try {
            const profile = (await dispatchPromise(
                getBuzzBridgeProfile({ skipRefresh: false }),
            )) as IBuzzBridgeProfileResponse;
            if (!profile?.data?.influencer?._id) {
                showToastMessage('Your creator profile is not ready yet. Connect an account and try again.', 'error');
                return;
            }
            await dispatchPromise(requestToJoinCampaign({ campaignId }));
            setLoggedInConnectOpen(false);
            setCreatorOAuthConnected({});
            if (campaignId) dispatch(getOffsiteCampaignDetails({ campaignId }));
            setSuccessOpen(true);
        } catch {
            // toast via saga
        } finally {
            setCreatorApplyBusy(false);
        }
    }, [
        campaignId,
        creatorOAuthConnected,
        dispatch,
        dispatchPromise,
        influencer,
        instagramAutoLinkEnabled,
        requiresSyncAutoLink,
    ]);

    const handleBackToDashboard = useCallback(() => {
        navigate(APP_ROUTES.ESDISCOVERY);
    }, [navigate]);

    const handleViewApplications = useCallback(() => {
        navigate(dashboardHubPath());
    }, [navigate]);

    const handleBackFromHero = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(APP_ROUTES.ESDISCOVERY);
        }
    }, [navigate]);

    const closeGuestApply = useCallback(() => {
        setGuestApplyOpen(false);
        setGuestPhase('form');
        setGuestEmail('');
        setGuestPassword('');
        setGuestPhone('');
        setGuestOAuthConnected({});
        setOauthConnectingGuest(null);
        setOtpLoading(false);
        setOtpResendInProgress(false);
        clearInfluencerPrelinkSession();
        clearPendingBuzzBridgeApplyCampaignId();
    }, []);

    const closeLoggedInConnect = useCallback(() => {
        setLoggedInConnectOpen(false);
        setCreatorOAuthConnected({});
        setLoadingConnectedPlatforms(false);
        setOauthConnectingCreator(null);
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: t.highlight }} />
            </Box>
        );
    }

    if (!campaign) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '60vh',
                    gap: 2,
                    px: 3,
                }}>
                <Typography sx={{ color: t.true_white, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>
                    Couldn&apos;t load this campaign
                </Typography>
                <Typography sx={{ color: t.subtle_white, fontSize: 14, textAlign: 'center' }}>
                    Check your connection and try again.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => campaignId && dispatch(getOffsiteCampaignDetails({ campaignId }))}
                    sx={{
                        bgcolor: t.highlight,
                        color: t.deep_grey_lighter,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                    }}>
                    Retry
                </Button>
            </Box>
        );
    }

    const brandName = typeof campaign.brand === 'object' && campaign.brand?.name ? campaign.brand.name : campaign.title;

    const requiredOauthPlatforms = resolveRequiredOauthPlatforms(campaign);
    const syncRequiredPlatforms: SyncOauthPlatformId[] = requiredOauthPlatforms.length
        ? requiredOauthPlatforms
        : ['instagram'];
    const isAutoLinkedForSync: boolean | null = !requiresSyncAutoLink
        ? null
        : !isSignedIn
        ? null
        : syncRequiredPlatforms.every(
              (p: SyncOauthPlatformId) =>
                  Boolean(creatorOAuthConnected[p]) ||
                  (influencer?.[p as keyof typeof influencer] as { linked?: boolean } | undefined)?.linked === true,
          );
    const missingOauthPlatforms = requiredOauthPlatforms.filter(
        (p: SyncOauthPlatformId) =>
            !(
                Boolean(creatorOAuthConnected[p]) ||
                (influencer?.[p as keyof typeof influencer] as { linked?: boolean } | undefined)?.linked === true
            ),
    );

    const applyBlocked = isPublicCampaignParticipationBlocked(campaign);
    const premiumAccess = effectivePremiumAccess ?? campaign?.premium_access;
    const premiumBlocked = Boolean(isSignedIn && premiumAccess?.premium && premiumAccess.can_apply === false);
    const syncLinkBlocked = Boolean(requiresSyncAutoLink && isSignedIn && isAutoLinkedForSync === false);

    const applyDisabled = requesting || (isSignedIn && gettingBuzzProfile) || applyBlocked;
    // Subscription gate CTAs stay highlighted — trial or Apply both open the plans modal.
    const applyLooksMuted = subscriptionRequiredGate ? false : applyBlocked || premiumBlocked || syncLinkBlocked;
    const applyLabel = showTrialApplyCta
        ? resolveCreatorTrialApplyCtaLabel(creatorTrial)
        : subscriptionRequiredGate
        ? getPublicCampaignApplyLabel(campaign)
        : premiumBlocked
        ? premiumAccess?.blocked_reason_code === 'instagram_automation_scopes'
            ? 'Reconnect Instagram'
            : premiumAccess?.blocked_reason_code === 'connected_accounts_required'
            ? 'Connect accounts'
            : 'Requirements not met'
        : syncLinkBlocked
        ? 'Auto-link accounts'
        : getPublicCampaignApplyLabel(campaign);
    const viewerPendingInvite = campaign.viewer_pending_invite;
    const showInviteCtas = Boolean(isSignedIn && viewerPendingInvite?._id);
    const showMobileReferralDock =
        device.isMobile &&
        Boolean(campaign._id) &&
        isCampaignReferralEarnVisible(campaign.referral_earnings_if_friend_hired);

    const applyButtonSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: 4,
        py: 2,
        background: applyLooksMuted
            ? 'rgba(255,255,255,0.12)'
            : `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange})`,
        color: applyLooksMuted ? 'rgba(255,255,255,0.85)' : t.button_text,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontWeight: 800,
        fontSize: 14,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        borderRadius: 10,
        boxShadow: applyLooksMuted ? 'none' : `0 0 20px ${t.highlight}4D`,
        '&:active': { transform: applyBlocked ? 'none' : 'scale(0.95)' },
        transition: 'all 0.2s',
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.45)',
            bgcolor: 'rgba(255,255,255,0.12)',
        },
    } as const;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                pb: {
                    xs: showMobileReferralDock ? 'calc(34dvh + env(safe-area-inset-bottom))' : 20,
                    md: 16,
                },
            }}>
            <Dialog
                open={guestApplyOpen}
                onClose={closeGuestApply}
                fullScreen={device.isMobile}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        bgcolor: t.deep_grey_lighter,
                        backgroundImage: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        ...(device.isMobile ? {} : { maxHeight: 'min(92vh, 900px)', borderRadius: 3 }),
                    },
                }}>
                <DialogContent
                    sx={{
                        p: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        pb: { xs: 'max(24px, env(safe-area-inset-bottom))', sm: 4 },
                        overflowY: 'auto',
                        flex: '1 1 auto',
                        minHeight: 0,
                    }}>
                    {guestPhase === 'form' && (
                        <CampaignApplicationForm
                            variant="modal"
                            flowType="guest"
                            verificationBrandName={brandName}
                            showAccountFields
                            firstName={guestFirstName}
                            lastName={guestLastName}
                            onFirstNameChange={setGuestFirstName}
                            onLastNameChange={setGuestLastName}
                            email={guestEmail}
                            password={guestPassword}
                            phone={guestPhone}
                            onEmailChange={setGuestEmail}
                            onPasswordChange={setGuestPassword}
                            onPhoneChange={setGuestPhone}
                            oauthConnected={guestOAuthConnected}
                            connectingPlatform={oauthConnectingGuest}
                            onOAuthConnecting={handleGuestOAuthConnecting}
                            onOAuthCode={handleGuestOAuthCode}
                            onOAuthFailed={handleGuestOAuthFailed}
                            onSubmit={() => void handleGuestSubmit()}
                            submitBusy={guestSubmitBusy}
                            disableSubmit={!guestInstagramReady}
                            requireOauthAutoLink={requiresSyncAutoLink}
                            instagramManualData={guestInstagramManualData}
                            onInstagramManualDataChange={setGuestInstagramManualData}
                            submitLabel="Create Account"
                            onBack={closeGuestApply}
                            onGuestLogInPress={() => {
                                if (campaignId) {
                                    setPendingBuzzBridgeApplyCampaignId(campaignId);
                                }
                                closeGuestApply();
                                dispatch(toggleAuthModal(true));
                            }}
                        />
                    )}
                    {guestPhase === 'otp' && (
                        <CampaignApplicationOTP
                            email={guestEmail}
                            onVerify={code => void handleGuestVerifyOTP(code)}
                            onResend={handleGuestResendOTP}
                            onBack={() => setGuestPhase('form')}
                            loading={otpLoading}
                            resendInProgress={otpResendInProgress}
                        />
                    )}
                    {guestPhase === 'success' && (
                        <CampaignApplicationSuccess
                            variant="modal"
                            campaign={campaign}
                            onBackToDashboard={() => {
                                closeGuestApply();
                                handleBackToDashboard();
                            }}
                            onViewApplications={() => {
                                closeGuestApply();
                                handleViewApplications();
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={loggedInConnectOpen}
                onClose={closeLoggedInConnect}
                fullScreen={device.isMobile}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        bgcolor: t.deep_grey_lighter,
                        backgroundImage: 'none',
                        ...(device.isMobile ? {} : { maxHeight: 'min(90vh, 880px)', borderRadius: 3 }),
                    },
                }}>
                <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
                    <CampaignApplicationForm
                        variant="modal"
                        flowType="logged_in_connect"
                        verificationBrandName={brandName}
                        showAccountFields={false}
                        oauthConnected={creatorOAuthConnected}
                        loadingPlatforms={loadingConnectedPlatforms}
                        connectingPlatform={oauthConnectingCreator}
                        onOAuthConnecting={handleCreatorOAuthConnecting}
                        onOAuthCode={handleCreatorOAuthCode}
                        onOAuthFailed={handleCreatorOAuthFailed}
                        onSubmit={() => void handleLoggedInApplySubmit()}
                        submitBusy={creatorApplyBusy}
                        disableSubmit={
                            requiredOauthPlatforms.length &&
                            (requiresSyncAutoLink || Boolean(premiumAccess?.connected_accounts_required))
                                ? missingOauthPlatforms.length > 0
                                : requiresSyncAutoLink || instagramAutoLinkEnabled
                                ? Object.keys(creatorOAuthConnected).length === 0
                                : !influencer?.instagram?.username
                        }
                        requireOauthAutoLink={requiresSyncAutoLink}
                        requiredOauthPlatforms={
                            requiresSyncAutoLink || premiumAccess?.connected_accounts_required
                                ? requiredOauthPlatforms
                                : undefined
                        }
                        onBack={closeLoggedInConnect}
                        instagramManualData={creatorInstagramManualData}
                        onInstagramManualDataChange={setCreatorInstagramManualData}
                        onManualPlatformSave={handleCreatorManualPlatformSave}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={successOpen}
                onClose={() => setSuccessOpen(false)}
                fullScreen={device.isMobile}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        bgcolor: t.deep_grey_lighter,
                        backgroundImage: 'none',
                        ...(device.isMobile ? {} : { maxHeight: 'min(90vh, 800px)', borderRadius: 3 }),
                    },
                }}>
                <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
                    <CampaignApplicationSuccess
                        variant="modal"
                        campaign={campaign}
                        onBackToDashboard={() => {
                            setSuccessOpen(false);
                            handleBackToDashboard();
                        }}
                        onViewApplications={() => {
                            setSuccessOpen(false);
                            handleViewApplications();
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Profile picture nudge */}
            <Dialog
                open={profilePicNudgeOpen}
                onClose={() => setProfilePicNudgeOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: t.deep_grey_lighter,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        p: 1,
                    },
                }}>
                <DialogContent>
                    <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white }}>
                            Add a profile picture
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: t.placeholderGray }}>
                            Creators with a profile photo are more likely to get noticed by brands. Add one before
                            applying to boost your chances.
                        </Typography>
                        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    setProfilePicNudgeOpen(false);
                                    void proceedToApply();
                                }}
                                sx={{
                                    borderColor: t.form_divider,
                                    color: t.placeholderGray,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    '&:hover': { borderColor: t.primary_white, color: t.primary_white },
                                }}>
                                Skip & Apply
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => {
                                    setProfilePicNudgeOpen(false);
                                    setEditBasicInfoForApplyOpen(true);
                                }}
                                sx={{
                                    bgcolor: t.highlight,
                                    color: t.deep_grey_lighter,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: t.highlight, opacity: 0.9 },
                                }}>
                                Add Photo
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Shipping address nudge for gifted campaigns */}
            <Dialog
                open={shippingNudgeOpen}
                onClose={() => setShippingNudgeOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: t.deep_grey_lighter,
                        backgroundImage: 'none',
                        borderRadius: 3,
                        p: 1,
                    },
                }}>
                <DialogContent>
                    <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white }}>
                            Shipping Address Required
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: t.placeholderGray }}>
                            This campaign ships a physical product to you. Please add your shipping address before
                            applying.
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => {
                                setShippingNudgeOpen(false);
                                setEditBasicInfoForApplyOpen(true);
                            }}
                            sx={{
                                bgcolor: t.highlight,
                                color: t.deep_grey_lighter,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'none',
                                borderRadius: 2,
                                '&:hover': { bgcolor: t.highlight, opacity: 0.9 },
                            }}>
                            Add Shipping Address
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* Edit basic info from apply nudge */}
            {influencer && (
                <EditBasicInfoModal
                    open={editBasicInfoForApplyOpen}
                    onClose={() => setEditBasicInfoForApplyOpen(false)}
                    influencer={influencer}
                    onSave={handleSaveBasicInfoForApply}
                />
            )}

            {/* Subscription nudge from apply flow */}
            <AccountSubscriptionModal
                open={subscriptionNudgeOpen}
                saveAmountFormatted={subscriptionSaveAmountFormatted}
                heroVariant={
                    giftedSubNudge ? 'save' : subscriptionNudgeRequired ? (trialAvailable ? 'trial' : 'pro') : 'save'
                }
                {...(giftedSubNudge && {
                    heroPill: 'Subscription required',
                    heroTitle: (
                        <>
                            A subscription is required to{' '}
                            <Box component="span" sx={{ color: '#ffc965' }}>
                                apply
                            </Box>
                        </>
                    ),
                    heroSubcopy:
                        'This campaign gifts a physical product — subscribe to unlock access, plus these perks:',
                })}
                campaignImageUrl={campaign?.feature_image ?? campaign?.assets?.[0]?.url}
                paystackCallbackUrl={campaignId ? `${window.location.origin}/campaigns/${campaignId}` : undefined}
                requireAllEntitlements={premiumUnlockEntitlements}
                onClose={() => {
                    setSubscriptionNudgeOpen(false);
                    setSubscriptionNudgeRequired(false);
                    setGiftedSubNudge(false);
                }}
                onDone={() => {
                    const wasGifted = giftedSubNudge;
                    setSubscriptionNudgeOpen(false);
                    setSubscriptionNudgeRequired(false);
                    setGiftedSubNudge(false);
                    void (async () => {
                        if (campaignId) {
                            try {
                                await dispatchPromise(getOffsiteCampaignDetails({ campaignId }));
                            } catch {
                                /* proceed with whatever we have */
                            }
                        }
                        // For gifted campaigns re-run handleApply so the shipping address guard is checked.
                        if (wasGifted) {
                            await handleApply();
                        } else {
                            await proceedToApply();
                        }
                    })();
                }}
                onSkip={
                    subscriptionNudgeRequired || giftedSubNudge
                        ? undefined
                        : () => {
                              setSubscriptionNudgeOpen(false);
                              setSubscriptionNudgeRequired(false);
                              void proceedToApply();
                          }
                }
            />

            <CampaignDetailHero campaign={campaign} onBackButtonPressed={handleBackFromHero} />

            <Box sx={{ maxWidth: 1152, mx: 'auto', px: { xs: 3, md: 4 }, mt: 6 }}>
                <Grid container spacing={6}>
                    <Grid item xs={12} lg={8}>
                        <CampaignDetailContent
                            campaign={campaign}
                            isSignedIn={Boolean(isSignedIn)}
                            isAutoLinked={requiresSyncAutoLink ? isAutoLinkedForSync : null}
                            premiumAccess={premiumAccess}
                            onPremiumSubscribe={openPlansModal}
                            onPremiumConnectAccounts={() => {
                                setCreatorOAuthConnected({});
                                setLoggedInConnectOpen(true);
                                fetchConnectedPlatforms();
                            }}
                            onPremiumSignIn={() => dispatch(toggleAuthModal(true))}
                            onSyncConnectAccounts={() => {
                                setCreatorOAuthConnected({});
                                setLoggedInConnectOpen(true);
                                fetchConnectedPlatforms();
                            }}
                            onSyncSignIn={() => dispatch(toggleAuthModal(true))}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <CampaignDetailRoles campaign={campaign} />
                    </Grid>
                </Grid>
            </Box>

            {showMobileReferralDock && campaign.referral_earnings_if_friend_hired && campaign._id ? (
                <Box
                    sx={{
                        display: { xs: 'flex', lg: 'none' },
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 60,
                        flexDirection: 'column',
                        gap: 1.25,
                        px: 2,
                        pt: 1.5,
                        pb: 'max(12px, env(safe-area-inset-bottom))',
                        maxHeight: '34dvh',
                        bgcolor: t.deep_grey_lighter,
                        borderTop: `1px solid rgba(255, 201, 101, 0.18)`,
                        boxShadow: '0 -12px 40px rgba(0, 0, 0, 0.55)',
                        backdropFilter: 'blur(12px)',
                    }}>
                    <CampaignReferralEarnSidebar
                        campaignId={campaign._id}
                        earnings={campaign.referral_earnings_if_friend_hired}
                        isSignedIn={Boolean(isSignedIn)}
                        variant="mobileDock"
                    />
                    {showInviteCtas ? (
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={inviteActionBusy}
                                onClick={() => void handleDeclinePendingInvite()}
                                sx={{
                                    py: 2,
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    borderColor: 'rgba(255,255,255,0.25)',
                                    color: 'rgba(255,255,255,0.85)',
                                }}>
                                Decline
                            </Button>
                            <Button
                                fullWidth
                                disabled={inviteActionBusy}
                                onClick={() => void handleAcceptPendingInvite()}
                                sx={applyButtonSx}>
                                {inviteActionBusy ? (
                                    <CircularProgress size={20} sx={{ color: t.button_text }} />
                                ) : (
                                    'Accept invite'
                                )}
                            </Button>
                        </Box>
                    ) : (
                        <Button fullWidth onClick={handleApplyOrTrialCta} disabled={applyDisabled} sx={applyButtonSx}>
                            {requesting ? <CircularProgress size={20} sx={{ color: t.button_text }} /> : applyLabel}
                            {!requesting && !applyBlocked && <Send sx={{ fontSize: 18 }} />}
                        </Button>
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: device.isMobile ? 'calc(12px + env(safe-area-inset-bottom))' : 94,
                        right: device.isMobile ? 16 : 28,
                        zIndex: 60,
                        display: 'flex',
                        gap: 1,
                    }}>
                    {showInviteCtas ? (
                        <>
                            <Button
                                variant="outlined"
                                disabled={inviteActionBusy}
                                onClick={() => void handleDeclinePendingInvite()}
                                sx={{
                                    py: 2,
                                    px: 3,
                                    borderRadius: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    borderColor: 'rgba(255,255,255,0.25)',
                                    color: 'rgba(255,255,255,0.85)',
                                    bgcolor: t.deep_grey_lighter,
                                }}>
                                Decline
                            </Button>
                            <Button
                                disabled={inviteActionBusy}
                                onClick={() => void handleAcceptPendingInvite()}
                                sx={applyButtonSx}>
                                {inviteActionBusy ? (
                                    <CircularProgress size={20} sx={{ color: t.button_text }} />
                                ) : (
                                    'Accept invite'
                                )}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={handleApplyOrTrialCta} disabled={applyDisabled} sx={applyButtonSx}>
                            {requesting ? <CircularProgress size={20} sx={{ color: t.button_text }} /> : applyLabel}
                            {!requesting && !applyBlocked && <Send sx={{ fontSize: 18 }} />}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};
